<!--
	CN-8312: Kanban View — Compact card layout for V3.

	NOTE: this inner wrapper deliberately does NOT use the .kanban-card class.
	pannel-wrapper.scss and procured-carriers.scss both define a global
	.kanban-card rule (both components use ViewEncapsulation.None) with
	padding / border / background / border-radius. The outer wrapper in
	pannel-wrapper.html already carries .kanban-card and owns those visuals;
	if this inner div also matched .kanban-card it would inherit a second
	bordered/padded box, producing the nested-card look the user saw in V3
	Recommended that V2/V1/Procured/Engaged cards never had. Using a distinct
	class (.kanban-card-body) decouples the inner layout from the outer
	visual chrome.
-->
<div *ngIf="isKanbanView && !utilService.isNullOrUndefined(searchResult)" class="kanban-card-body">
	<!-- Row 1: Carrier Name/Icons, MC/DOT, Match Confidence -->
	<div class="kanban-row">
		<div class="kanban-cell carrier-name-cell">
			<span [class.font-weight-bold]="searchResult.customerCarrier" class="carrier-name ellipsis">
				{{ searchResult.businessName }}
			</span>
			<img
				*ngIf="
					SHOW_IN_NETWORK_LOGO(
						CARRIER_QUALITY_ENABLED,
						searchResult.firmDot,
						isCarrierVettingIntegrationEnabled,
						searchResult?.vettingSource,
						searchResult.vettingSourceId,
						carrierVettingService,
						searchResult.carrierId,
						searchResult.customerCarrier
					)
				"
				[src]="carrierVettingService.logo(searchResult?.vettingSource)"
				[matTooltip]="
					carrierVettingService.tooltip(
						searchResult?.vettingSource,
						searchResult?.carrierOnboardedOn,
						searchResult?.lastVettingTime
					)
				"
				class="kanban-icon"
			/>
			<span
				*ngIf="
					isIntegrationLinkAvailable &&
					SHOW_ONBOARD_CARRIER_LINK(
						CARRIER_QUALITY_ENABLED,
						carrierVettingService,
						searchResult?.vettingSource,
						searchResult.customerCarrier,
						searchResult.firmDot,
						isCarrierVettingIntegrationEnabled,
						searchResult.vettingSourceId,
						isCarrierOnboarded
					)
				"
				class="link font-size-11"
				tabindex="0"
				role="button"
				(click)="$event.stopImmediatePropagation(); onboardCarrier()"
				(keydown.enter)="$event.stopImmediatePropagation(); onboardCarrier()"
				(keydown.space)="$event.preventDefault(); $event.stopImmediatePropagation(); onboardCarrier()"
			>
				Onboard
			</span>
		</div>
		<div class="kanban-cell ellipsis" (click)="$event.stopPropagation()">
			<span *ngIf="searchResult.firmMc">MC: {{ searchResult.firmMc }}</span>
			<span *ngIf="searchResult.firmMc && searchResult.firmDot"> / </span>
			<span *ngIf="searchResult.firmDot">DOT: {{ searchResult.firmDot }}</span>
		</div>
		<div class="kanban-cell">
			<meter
				high="66"
				low="33"
				max="100"
				min="0"
				optimum="90"
				[value]="searchResult.match"
				[matTooltip]="'Match: ' + searchResult.match + '%'"
				class="kanban-meter"
			></meter>
		</div>
	</div>

	<!-- Row 2: PU/T/D, Authority, Quality Score
	     CN-8312 V3 parity: dropped the trailing "/ C: contacts" stat so this row
	     matches the procured-carriers Kanban card layout. -->
	<div class="kanban-row">
		<div class="kanban-cell ellipsis">
			PU: {{ searchResult.powerUnits ?? 'N/A' }} / T: {{ searchResult.trailers ?? 'N/A' }} / D: {{ searchResult.drivers ?? 'N/A' }}
		</div>
		<div class="kanban-cell ellipsis">
			Auth: {{ searchResult.authorityGrantDate || 'N/A' }}
		</div>
		<div class="kanban-cell" *ngIf="accountSettings.CARRIER_QUALITY">
			<span class="font-weight-bold {{ getCcScoreDisplay(searchResult.ccScore) || 'N/A' }}">
				{{ getCcScoreDisplay(searchResult.ccScore) || 'N/A' }}
			</span>
		</div>
	</div>

	<!-- Row 3: Lane / HQ, Last Load, Last Rate
	     CN-8312 V3 parity: dropped the "Lane:" / "HQ:" prefixes so cells display
	     just the value (matches the bare lanePath shown on procured/engaged
	     Kanban cards). HQ fallback kept because recommended carriers may not
	     have a lane match yet — preferable to a blank cell. -->
	<div class="kanban-row">
		<div class="kanban-cell ellipsis" *ngIf="searchResult.isLane && searchResult.lanePath">
			{{ searchResult.lanePath }}
		</div>
		<div class="kanban-cell ellipsis" *ngIf="!searchResult.isLane || !searchResult.lanePath">
			{{ searchResult.carrierHQ || 'No Lane' }}
		</div>
		<div class="kanban-cell ellipsis">
			Last: {{ searchResult.rateUpdatedAt ? (searchResult.rateUpdatedAt | date: 'MM/dd/yy') : 'N/A' }}
		</div>
		<div class="kanban-cell ellipsis">
			Rate: {{ IS_NOT_NULL_OR_UNDEFINED(searchResult.rate) ? (searchResult.rate | currency: 'USD' : 'symbol' : '1.0-0') : 'N/A' }}
		</div>
	</div>
</div>

<!-- Standard View (V1/V2/V3 non-Kanban) -->
<mat-panel-title *ngIf="!isKanbanView" class="primary-text" style="width: 100%">
	<table *ngIf="!utilService.isNullOrUndefined(searchResult)" class="full-width" fxFlex="100">
		<tr fxLayout="row">
			<td
				#businessNameContainer
				[fxFlex]="hideLastColumn ? (isAssignRepColumnVisibleInV2 ? '25' : '28') : ''"
				fxLayout="row"
				fxLayoutAlign="space-between center"
				class="p-r-1"
			>
				<div fxLayout="row">
					<div
						[ngStyle]="{
							'max-width':
								'calc(' +
								(businessNameContainer?.offsetWidth
									? businessNameContainer.offsetWidth -
										(20 +
											(isIntegrationLinkAvailable &&
											SHOW_ONBOARD_CARRIER_LINK(
												CARRIER_QUALITY_ENABLED,
												carrierVettingService,
												searchResult?.vettingSource,
												searchResult.customerCarrier,
												searchResult.firmDot,
												isCarrierVettingIntegrationEnabled,
												searchResult.vettingSourceId,
												isCarrierOnboarded
											)
												? 90
												: 16))
									: 240) +
								'px)',
						}"
						class="ellipsis"
					>
						<b *ngIf="searchResult.customerCarrier" class="font-size-16">
							{{ searchResult.businessName }}
						</b>
						<span *ngIf="!searchResult.customerCarrier" class="font-size-16">
							{{ searchResult.businessName }}
						</span>
					</div>
					<div
						*ngIf="
							SHOW_IN_NETWORK_LOGO(
								CARRIER_QUALITY_ENABLED,
								searchResult.firmDot,
								isCarrierVettingIntegrationEnabled,
								searchResult?.vettingSource,
								searchResult.vettingSourceId,
								carrierVettingService,
								searchResult.carrierId,
								searchResult.customerCarrier
							)
						"
						fxLayoutAlign="start center"
					>
						<img
							[src]="carrierVettingService.logo(searchResult?.vettingSource)"
							[matTooltip]="
								carrierVettingService.tooltip(
									searchResult?.vettingSource,
									searchResult?.carrierOnboardedOn,
									searchResult?.lastVettingTime
								)
							"
							class="h-18 in_network_logo"
						/>
					</div>
					<img
						*ngIf="searchResult?.haulPayApproved && environment.SHOW_QUALITY"
						class="carrier_header_icons"
						[src]="haulpayService.logo()"
						[matTooltip]="haulpayService.tooltip()"
					/>
				</div>
				<span
					*ngIf="
						isIntegrationLinkAvailable &&
						SHOW_ONBOARD_CARRIER_LINK(
							CARRIER_QUALITY_ENABLED,
							carrierVettingService,
							searchResult?.vettingSource,
							searchResult.customerCarrier,
							searchResult.firmDot,
							isCarrierVettingIntegrationEnabled,
							searchResult.vettingSourceId,
							isCarrierOnboarded
						)
					"
					class="link ml-6 font-size-12"
					fxLayoutAlign="end center"
					(click)="$event.stopImmediatePropagation(); onboardCarrier()"
				>
					Onboard Carrier
				</span>
			</td>

			<!-- V1 Layout: Quality Score in column 2 -->
			<td *ngIf="!hideLastColumn" fxFlex="20" fxLayoutAlign="start center">
				<div
					class="full-width p-r-1"
					(click)="$event.stopPropagation()"
					fxLayoutAlign="space-between center"
					*ngIf="accountSettings.CARRIER_QUALITY"
				>
					<span class="font-size-14 font-weight-bold">Quality Score: </span>
					<span
						class="font-size-14 font-weight-bold {{
							getCcScoreDisplay(searchResult.ccScore) || 'N/A'
						}}"
						>{{ getCcScoreDisplay(searchResult.ccScore) || 'N/A' }}</span
					>
				</div>
			</td>

			<!-- V2/V3 Layout: Quality Score in column 2 -->
			<td *ngIf="hideLastColumn" [fxFlex]="isAssignRepColumnVisibleInV2 ? '17' : '19'" fxLayoutAlign="start center">
				<div
					class="full-width p-r-1 ellipsis"
					(click)="$event.stopPropagation()"
					*ngIf="accountSettings.CARRIER_QUALITY"
				>
					<span class="font-size-14 font-weight-bold">Quality Score:&nbsp;</span>
					<span
						class="font-size-14 font-weight-bold {{
							getCcScoreDisplay(searchResult.ccScore) || 'N/A'
						}}"
						>{{ getCcScoreDisplay(searchResult.ccScore) || 'N/A' }}</span
					>
				</div>
			</td>

			<!-- V1 Layout: Match Confidence in column 3 -->
			<td *ngIf="!hideLastColumn" fxFlex="21" fxLayoutAlign="start center">
				<div
					class="full-width"
					(click)="$event.stopPropagation()"
					fxLayoutAlign="space-between center"
				>
					<span
						class="font-size-14"
						[style.fontWeight]="filters.column == RANGE_FILTERS.SORT.MATCH ? 'bold' : 'normal'"
					>
						Match Confidence
					</span>
					<span class="font-size-14 font-weight-bold success-text">
						<meter
							high="66"
							low="33"
							max="100"
							min="0"
							optimum="90"
							value="{{ searchResult.match }}"
							[matTooltip]="searchResult.match"
							matTooltipClass="custom-tooltip"
						></meter>
					</span>
				</div>
			</td>

			<!-- V2/V3 Layout: Match Score in column 3 -->
			<td *ngIf="hideLastColumn" [fxFlex]="isAssignRepColumnVisibleInV2 ? '22' : '25'" fxLayoutAlign="start center">
				<div
					class="full-width p-r-1"
					(click)="$event.stopPropagation()"
					fxLayoutAlign="start center"
				>
					<span class="font-size-14 font-weight-bold text-nowrap">Match Score:&nbsp;</span>
					<span class="font-size-14 font-weight-bold success-text">
						<meter
							high="66"
							low="33"
							max="100"
							min="0"
							optimum="90"
							value="{{ searchResult.match }}"
							[matTooltip]="searchResult.match"
							matTooltipClass="custom-tooltip"
						></meter>
					</span>
				</div>
			</td>

			<!-- V1 Layout: Empty spacer column 4 -->
			<td *ngIf="!hideLastColumn" fxFlex="20"></td>

			<!-- V2/V3 Layout: Last Contact in row 1 col 4 (moved up from row 2) -->
			<td *ngIf="hideLastColumn" [fxFlex]="isAssignRepColumnVisibleInV2 ? '22' : '28'" fxLayoutAlign="start center">
				<div class="full-width p-r-1 ellipsis">
					<span class="font-size-14 font-weight-bold">Last Contact:&nbsp;</span>
					<span class="font-size-14">
						{{
							!utilService.isNullOrUndefined(searchResult.recentFeedbackDate)
								? DATE_MDY(searchResult.recentFeedbackDate)
								: 'N/A'
						}}
					</span>
				</div>
			</td>

			<!-- Column 5: Assign Rep — CN-8312 V2: only render when populated.
			     In V1 always render. Without this gate V2 row 1 sums to 119%
			     (28+19+25+28+19), the browser falls back to equal distribution,
			     and column widths break (carrier name + Lane + Contact truncate). -->
			<td
				*ngIf="
					!hideLastColumn ||
					(productsService.ASSIGN_CARRIERS &&
						!utilService.isNullOrUndefined(usersList) &&
						usersList.length > 0 &&
						!utilService.isNullOrUndefined(userCarrierMapList))
				"
				[fxFlex]="hideLastColumn ? '14' : '11'"
				fxLayout="row"
				fxLayoutAlign="start center"
				class="p-l-1"
			>
				<div
					fxFlex
					*ngIf="
						productsService.ASSIGN_CARRIERS &&
						!utilService.isNullOrUndefined(usersList) &&
						usersList.length > 0 &&
						!utilService.isNullOrUndefined(userCarrierMapList)
					"
				>
					<app-user-selector
						(userChanged)="setAssignee($event, searchResult.carrierId)"
						[selectedUsers]="
							utilService.isNullOrUndefined(userCarrierMap[searchResult.carrierId])
								? []
								: [getUserIdAssigned(searchResult.carrierId)]
						"
						[showAssignMe]="false"
						[singleselect]="true"
						[usersList]="usersList"
						[allUsersList]="allUsersList"
						customCssClass="custom-form-field assignn_rep_max_width"
						floatLabel="never"
						placeholder="Assign Rep"
					></app-user-selector>
				</div>
			</td>
		</tr>

		<tr class="font-size-14 mt-3" fxLayout="row">
			<td [fxFlex]="hideLastColumn ? (isAssignRepColumnVisibleInV2 ? '25' : '28') : ''" fxLayoutAlign="start center">
				<!-- V1 Layout: MC/DOT in row 2 col 1 -->
				<div *ngIf="!hideLastColumn" (click)="$event.stopPropagation()" fxLayoutAlign="end center">
					<span
						*ngIf="
							!utilService.isNullOrUndefined(searchResult.firmDot) ||
							!utilService.isNullOrUndefined(searchResult.firmMc)
						"
						class="font-size-14"
					>
						<span
							(click)="utilService.copyToClipboard(searchResult.firmMc, COPY_TO_CLIPBOARD_MSGS.MC)"
							*ngIf="!utilService.isNullOrUndefined(searchResult.firmMc)"
						>
							MC: {{ searchResult.firmMc }}
						</span>
						{{
							!utilService.isNullOrUndefined(searchResult.firmDot) &&
							!utilService.isNullOrUndefined(searchResult.firmMc)
								? ' / '
								: ''
						}}
						<span
							(click)="
								utilService.copyToClipboard(searchResult.firmDot, COPY_TO_CLIPBOARD_MSGS.DOT)
							"
							*ngIf="!utilService.isNullOrUndefined(searchResult.firmDot)"
						>
							DOT: {{ searchResult.firmDot }}
						</span>
					</span>
				</div>
				<!-- V2/V3 Layout: MC/DOT in row 2 col 1 (under carrier name, above PU/T/D/C) -->
				<div *ngIf="hideLastColumn" (click)="$event.stopPropagation()" fxLayoutAlign="start center">
					<span
						*ngIf="
							!utilService.isNullOrUndefined(searchResult.firmDot) ||
							!utilService.isNullOrUndefined(searchResult.firmMc)
						"
						class="font-size-14"
					>
						<span
							(click)="utilService.copyToClipboard(searchResult.firmMc, COPY_TO_CLIPBOARD_MSGS.MC)"
							*ngIf="!utilService.isNullOrUndefined(searchResult.firmMc)"
						>
							<span class="font-weight-bold">MC:</span> {{ searchResult.firmMc }}
						</span>
						{{
							!utilService.isNullOrUndefined(searchResult.firmDot) &&
							!utilService.isNullOrUndefined(searchResult.firmMc)
								? ' / '
								: ''
						}}
						<span
							(click)="
								utilService.copyToClipboard(searchResult.firmDot, COPY_TO_CLIPBOARD_MSGS.DOT)
							"
							*ngIf="!utilService.isNullOrUndefined(searchResult.firmDot)"
						>
							<span class="font-weight-bold">DOT:</span> {{ searchResult.firmDot }}
						</span>
					</span>
				</div>
			</td>
			<!-- V1 Layout: Column widths -->
			<td *ngIf="!hideLastColumn" fxFlex="20" fxLayoutAlign="start center">
				<div class="full-width p-r-1" fxLayoutAlign="space-between center">
					<span>Authority:</span>
					<span>{{ searchResult?.authorityGrantDate || '' }}</span>
				</div>
			</td>
			<!-- V2/V3 Layout: Adjusted column widths -->
			<td *ngIf="hideLastColumn" [fxFlex]="isAssignRepColumnVisibleInV2 ? '17' : '19'" fxLayoutAlign="start center">
				<div class="full-width p-r-1 ellipsis">
					<span class="font-size-14 font-weight-bold">Authority:&nbsp;</span>
					<span class="font-size-14">{{ searchResult?.authorityGrantDate || 'N/A' }}</span>
				</div>
			</td>
			<!-- V1 Layout: Lane in row 2 col 3 -->
			<td *ngIf="!hideLastColumn" fxFlex="21" fxLayoutAlign="start center">
				<div class="full-width" fxLayoutAlign="start center" *ngIf="searchResult.isLane">
					<span>Lane:&nbsp;</span>
					<span
						class="font-size-12 truncate_text"
						[appTruncateTooltip]="
							searchResult.lanePath +
								(!utilService.isNullOrUndefined(LANE_TRAILER_TYPE)
									? '(' + LANE_TRAILER_TYPE + ')'
									: '') || ''
						"
					>
						{{ searchResult.lanePath }}
						{{
							!utilService.isNullOrUndefined(LANE_TRAILER_TYPE) ? '(' + LANE_TRAILER_TYPE + ')' : ''
						}}
					</span>
				</div>
				<span *ngIf="isSearchResultOnlyTruck()">{{ searchResult.location }}</span>
				<span *ngIf="searchResult.isTruckAndLane">
					<img height="18px" src="./assets/icons/delivery-truck.svg" style="margin: 0 6px -4px 0" />
				</span>
			</td>
			<!-- V2/V3 Layout: Lane in row 2 col 3 -->
			<td *ngIf="hideLastColumn" [fxFlex]="isAssignRepColumnVisibleInV2 ? '22' : '25'" fxLayoutAlign="start center">
				<div
					class="full-width p-r-1 ellipsis"
					*ngIf="searchResult.isLane"
					[matTooltip]="
						searchResult.lanePath +
							(!utilService.isNullOrUndefined(LANE_TRAILER_TYPE)
								? ' (' + LANE_TRAILER_TYPE + ')'
								: '') || ''
					"
					matTooltipClass="custom-tooltip"
				>
					<span class="font-size-14 font-weight-bold">Lane:&nbsp;</span>
					<span class="font-size-14">
						{{ searchResult.lanePath }}{{
							!utilService.isNullOrUndefined(LANE_TRAILER_TYPE) ? ' (' + LANE_TRAILER_TYPE + ')' : ''
						}}
					</span>
				</div>
				<div class="full-width p-r-1 ellipsis" *ngIf="isSearchResultOnlyTruck()">
					<span class="font-size-14">{{ searchResult.location }}</span>
				</div>
				<span *ngIf="searchResult.isTruckAndLane">
					<img height="18px" src="./assets/icons/delivery-truck.svg" style="margin: 0 6px -4px 0" />
				</span>
			</td>
			<!-- V1 Layout: Last Contact in row 2 col 4 -->
			<td *ngIf="!hideLastColumn" fxFlex="20">
				<div class="full-width p-r-1 p-l-1" fxLayoutAlign="space-between center">
					<span>Last Contact:</span>
					<span>
						{{
							!utilService.isNullOrUndefined(searchResult.recentFeedbackDate)
								? DATE_MDY(searchResult.recentFeedbackDate)
								: 'N/A'
						}}
					</span>
				</div>
			</td>
			<!-- V2/V3 Layout: Contact name in row 2 col 4 -->
			<td *ngIf="hideLastColumn" [fxFlex]="isAssignRepColumnVisibleInV2 ? '22' : '28'" fxLayoutAlign="start center">
				<div class="full-width p-r-1 ellipsis">
					<span class="font-size-14 font-weight-bold">Contact:&nbsp;</span>
					<span class="font-size-14">
						{{ searchResult.representative1 || 'N/A' }}
					</span>
				</div>
			</td>
			<!-- V2/V3 Layout: Spacer column 5 -->
						<td fxFlex="11" fxLayoutAlign="start center" *ngIf="!hideLastColumn">
				<mat-spinner
					*ngIf="utilService.isNullOrUndefined(searchResult.lanesCount)"
					[diameter]="11"
					strokeWidth="1"
				></mat-spinner>
				<div
					class="full-width"
					*ngIf="!utilService.isNullOrUndefined(searchResult.lanesCount)"
					(click)="$event.stopPropagation(); showFullProfile()"
					fxLayoutAlign="space-between center"
				>
					<span>
						{{ searchResult.lanesCount + ' Lane' + (searchResult.lanesCount > 1 ? 's' : '') }}
					</span>
					<span fxLayoutAlign="start center">
						<img class="ml-4" height="18px" src="./assets/icons/road.svg" />
					</span>
				</div>
			</td>
			<!-- V2/V3 Layout: trailing placeholder reserved for the missing Assign Rep
			     slot in this row, so col widths align with row 1 across all rows. -->
			<td *ngIf="isAssignRepColumnVisibleInV2" fxFlex="14"></td>
		</tr>

		<tr class="font-size-14 mt-3" fxLayout="row">
			<td [fxFlex]="hideLastColumn ? (isAssignRepColumnVisibleInV2 ? '25' : '28') : ''" fxLayoutAlign="start center">
				<!-- V1 Layout: PU/T/D/C in row 3 col 1 -->
				<div *ngIf="!hideLastColumn">
					<span>PU: {{ searchResult.powerUnits ?? 'N/A' }} </span>
					<span>&nbsp;/ T: {{ searchResult.trailers ?? 'N/A' }} </span>
					<span>&nbsp;/ D: {{ searchResult.drivers ?? 'N/A' }} </span>
					<span>&nbsp;/ C: {{ searchResult.carrierContactsCount ?? 'N/A' }} </span>
				</div>
				<!-- V2/V3 Layout: PU/T/D/C in row 3 col 1 (after MC/DOT row) -->
				<div *ngIf="hideLastColumn">
					<span><span class="font-weight-bold">PU:</span> {{ searchResult.powerUnits ?? 'N/A' }} </span>
					<span>&nbsp;/ <span class="font-weight-bold">T:</span> {{ searchResult.trailers ?? 'N/A' }} </span>
					<span>&nbsp;/ <span class="font-weight-bold">D:</span> {{ searchResult.drivers ?? 'N/A' }} </span>
					<span>&nbsp;/ <span class="font-weight-bold">C:</span> {{ searchResult.carrierContactsCount ?? 'N/A' }} </span>
				</div>
			</td>
			<!-- V1 Layout: Column widths -->
			<td *ngIf="!hideLastColumn" fxFlex="20" fxLayoutAlign="start center">
				<div class="full-width p-r-1" fxLayoutAlign="space-between center">
					<span>Cargo Ins:</span>
					<span *ngIf="searchResult.cargoInsuranceAmount">
						{{ searchResult.cargoInsuranceAmount | currency: 'USD' : 'symbol' : '1.0-0' }}
					</span>
					<span *ngIf="!searchResult.cargoInsuranceAmount">N/A</span>
				</div>
			</td>
			<!-- V2/V3 Layout: Adjusted column widths -->
			<td *ngIf="hideLastColumn" [fxFlex]="isAssignRepColumnVisibleInV2 ? '17' : '19'" fxLayoutAlign="start center">
				<div class="full-width p-r-1 ellipsis">
					<span class="font-size-14 font-weight-bold">Insurance:&nbsp;</span>
					<span class="font-size-14" *ngIf="searchResult.cargoInsuranceAmount">
						{{ searchResult.cargoInsuranceAmount | currency: 'USD' : 'symbol' : '1.0-0' }}
					</span>
					<span class="font-size-14" *ngIf="!searchResult.cargoInsuranceAmount">N/A</span>
				</div>
			</td>
			<!-- V1 Layout: Frequency -->
			<td *ngIf="!hideLastColumn" #frequencyContainer fxFlex="21" fxLayoutAlign="start center">
				<div
					class="full-width ellipsis"
					fxLayoutAlign="space-between center"
					*ngIf="accountSettings.FREQUENCY_ENABLED && searchResult.isLane"
					[style.fontWeight]="filters.column === RANGE_FILTERS.SORT.FREQUENCY ? 'bold' : 'normal'"
					[ngStyle]="{
						'max-width':
							'calc(' +
							(frequencyContainer?.offsetWidth ? frequencyContainer.offsetWidth - 8 : 200) +
							'px)',
					}"
				>
					<span> Frequency: </span>
					<span>
						{{
							!utilService.isNullOrUndefined(searchResult.frequency) ? searchResult.frequency : ''
						}}
						{{
							!utilService.isNullOrUndefined(searchResult.frequency) &&
							isFrequencyValidNumber(searchResult.frequency)
								? '/ weekly'
								: ''
						}}
					</span>
				</div>
			</td>

			<!-- V1 Layout: Last Load in row 3 col 4 -->
			<td *ngIf="!hideLastColumn" fxFlex="20" fxLayoutAlign="start center">
				<div class="full-width p-r-1 p-l-1" fxLayoutAlign="space-between center">
					<span>Last Load: </span>
					<span *ngIf="searchResult.rateUpdatedAt">
						&nbsp;
						{{ searchResult.rateUpdatedAt | date: 'MM/dd/yyyy' }}
					</span>
				</div>
			</td>
			<!-- V2/V3 Layout: Last Load in row 3 col 3 -->
			<td *ngIf="hideLastColumn" [fxFlex]="isAssignRepColumnVisibleInV2 ? '22' : '25'" fxLayoutAlign="start center">
				<div class="full-width p-r-1 ellipsis">
					<span class="font-size-14 font-weight-bold">Last Load:&nbsp;</span>
					<span class="font-size-14">
						{{ searchResult.rateUpdatedAt ? (searchResult.rateUpdatedAt | date: 'MM/dd/yyyy') : 'N/A' }}
					</span>
				</div>
			</td>
			<!-- V2/V3 Layout: Email in row 3 col 4 (below Contact) -->
			<td *ngIf="hideLastColumn" [fxFlex]="isAssignRepColumnVisibleInV2 ? '22' : '28'" fxLayoutAlign="start center">
				<div class="full-width p-r-1 ellipsis">
					<span class="font-size-14 font-weight-bold">Email:&nbsp;</span>
					<span class="font-size-14" *ngIf="searchResult.businessEmailAddress">
						<a
							href="mailto:{{ searchResult.businessEmailAddress }}"
							(click)="$event.stopPropagation()"
						>{{ searchResult.businessEmailAddress?.toLowerCase() }}</a>
					</span>
					<span class="font-size-14" *ngIf="!searchResult.businessEmailAddress">N/A</span>
				</div>
			</td>
			<!-- V2/V3 Layout: Spacer column 5 -->
						<td fxFlex="11" fxLayoutAlign="start center" *ngIf="!hideLastColumn">
				<mat-spinner
					*ngIf="utilService.isNullOrUndefined(searchResult.notesCount)"
					[diameter]="11"
					strokeWidth="1"
				></mat-spinner>

				<div *ngIf="!utilService.isNullOrUndefined(searchResult.notesCount)" class="full-width">
					<div
						*ngIf="searchResult.notesCount > 0"
						fxLayoutAlign="space-between center"
						class="full-width"
					>
						<span>
							{{ searchResult.notesCount + ' Note' + (searchResult.notesCount > 1 ? 's' : '') }}
						</span>
						<span fxLayoutAlign="start center">
							<img class="ml-4 px-2" height="18px" src="./assets/icons/sticky-note.svg" />
						</span>
					</div>
					<div
						*ngIf="searchResult.notesCount == 0"
						fxLayoutAlign="space-between center"
						class="full-width"
					>
						<span class="primary-text">No Notes</span>
						<span fxLayoutAlign="start center">
							<img class="ml-4 px-2" height="18px" src="./assets/icons/sticky-note.svg" />
						</span>
					</div>
				</div>
			</td>
			<!-- V2/V3 Layout: trailing placeholder reserved for the missing Assign Rep
			     slot in this row, so col widths align with row 1 across all rows. -->
			<td *ngIf="isAssignRepColumnVisibleInV2" fxFlex="14"></td>
		</tr>

		<tr class="font-size-14 mt-3" fxLayout="row">
			<td [fxFlex]="hideLastColumn ? (isAssignRepColumnVisibleInV2 ? '25' : '28') : ''" fxLayoutAlign="start center">
				<!-- V1 Layout: HQ in row 4 col 1 -->
				<span
					*ngIf="!hideLastColumn && IS_NOT_EMPTY(searchResult.carrierHQ)"
					[style.fontWeight]="
						!utilService.isNullOrUndefined(filters.carrierHQ) && filters.carrierHQ.length > 2
							? 'bold'
							: 'normal'
					"
				>
					HQ: {{ searchResult.carrierHQ }}
					<span *ngIf="!utilService.isNullOrUndefined(searchResult.distance)" class="primary-text">
						({{ searchResult.distance }} miles)
					</span>
				</span>
				<!-- V2/V3 Layout: HQ in row 4 col 1 (was empty; pushed down by MC/DOT row) -->
				<span
					*ngIf="hideLastColumn && IS_NOT_EMPTY(searchResult.carrierHQ)"
					[style.fontWeight]="
						!utilService.isNullOrUndefined(filters.carrierHQ) && filters.carrierHQ.length > 2
							? 'bold'
							: 'normal'
					"
				>
					<span class="font-weight-bold">HQ:</span> {{ searchResult.carrierHQ }}
					<span *ngIf="!utilService.isNullOrUndefined(searchResult.distance)" class="primary-text">
						({{ searchResult.distance }} miles)
					</span>
				</span>
			</td>
			<!-- V2/V3 Layout: empty Col 2 placeholder so Last Rate / Phone align with Col 3 / Col 4 above -->
			<td *ngIf="hideLastColumn" [fxFlex]="isAssignRepColumnVisibleInV2 ? '17' : '19'"></td>
			<!-- V1 Layout: Column widths -->
			<td *ngIf="!hideLastColumn" fxFlex="20" fxLayoutAlign="start center">
				<div class="full-width p-r-1" fxLayoutAlign="space-between center">
					<span>Violations: </span>
					<span>N/A</span>
				</div>
			</td>
			<!-- V1 Layout: DH-O/DH-D in row 4 col 3 -->
			<td *ngIf="!hideLastColumn" fxFlex="21" fxLayoutAlign="start center">
				<div class="full-width" fxLayoutAlign="space-between center">
					<span>
						<span *ngIf="!utilService.isNullOrUndefined(searchResult.originDeadhead)">
							DH-O: {{ searchResult.originDeadhead | number: '1.0-0' }}
						</span>
						<span *ngIf="searchResult.originDeadhead && searchResult.destinationDeadhead">
							&nbsp;/&nbsp;
						</span>
					</span>
					<span>
						<span class="font-size-14">
							<span>DH-D: </span>
							<span *ngIf="!utilService.isNullOrUndefined(searchResult.destinationDeadhead)">
								{{ searchResult.destinationDeadhead | number: '1.0-0' }}
							</span>
							<span *ngIf="utilService.isNullOrUndefined(searchResult.destinationDeadhead)">
								N/A
							</span>
						</span>
					</span>
				</div>
			</td>
			<!-- V1 Layout: Last Rate in row 4 col 4 -->
			<td *ngIf="!hideLastColumn" fxFlex="20" fxLayoutAlign="start center">
				<div class="full-width p-r-1 p-l-1" fxLayoutAlign="space-between center">
					<span>Last Rate:</span>
					<span
						*ngIf="IS_NOT_NULL_OR_UNDEFINED(searchResult.rate)"
						[matTooltipDisabled]="!searchResult.ratePickupDate"
						matTooltip="Pickup Date of the load Rate: {{ DATE_MDY(searchResult.ratePickupDate) }}"
						matTooltipClass="custom-tooltip"
					>
						{{ searchResult.rate | currency: 'USD' : 'symbol' : '1.0-0' }}
					</span>
					<span *ngIf="IS_NULL_OR_UNDEFINED(searchResult.rate)"> N/A </span>
				</div>
			</td>
			<!-- V2/V3 Layout: Last Rate in row 4 col 3 (below Last Load) -->
			<td *ngIf="hideLastColumn" [fxFlex]="isAssignRepColumnVisibleInV2 ? '22' : '25'" fxLayoutAlign="start center">
				<div class="full-width p-r-1 ellipsis">
					<span class="font-size-14 font-weight-bold">Last Rate:&nbsp;</span>
					<span
						class="font-size-14"
						*ngIf="IS_NOT_NULL_OR_UNDEFINED(searchResult.rate)"
						[matTooltipDisabled]="!searchResult.ratePickupDate"
						matTooltip="Pickup Date of the load Rate: {{ DATE_MDY(searchResult.ratePickupDate) }}"
						matTooltipClass="custom-tooltip"
					>
						{{ searchResult.rate | currency: 'USD' : 'symbol' : '1.0-0' }}
					</span>
					<span class="font-size-14" *ngIf="IS_NULL_OR_UNDEFINED(searchResult.rate)">N/A</span>
				</div>
			</td>
			<!-- V2/V3 Layout: Phone in row 4 col 4 (below Email) -->
			<td *ngIf="hideLastColumn" [fxFlex]="isAssignRepColumnVisibleInV2 ? '22' : '28'" fxLayoutAlign="start center">
				<div class="full-width p-r-1 ellipsis">
					<span class="font-size-14 font-weight-bold">Phone:&nbsp;</span>
					<span class="font-size-14" *ngIf="searchResult.phone">
						<a href="tel:{{ searchResult.phone }}" (click)="$event.stopPropagation()">{{ searchResult.phone }}</a>
					</span>
					<span class="font-size-14" *ngIf="!searchResult.phone">N/A</span>
				</div>
			</td>
			<!-- V2/V3 Layout: Empty spacer column 5 -->
						<td fxFlex="11" fxLayoutAlign="start center" *ngIf="!hideLastColumn">
				<div class="full-width" *ngIf="productsService.PROJECT_CARRIERS">
					<div
						fxLayoutAlign="space-between center"
						class="full-width dropdown"
						*ngIf="searchResult.projectCount > 0"
					>
						<span>
							{{ searchResult.projectCount }}
							{{ searchResult.projectCount > 1 ? 'Projects' : 'Project' }}
						</span>
						<span fxLayoutAlign="start center">
							<mat-icon class="font-size-18 h-18 ml-4 pl-2 w-inherit">assignment</mat-icon>
						</span>
					</div>

					<div
						fxLayoutAlign="space-between center"
						class="full-width"
						*ngIf="searchResult.projectCount == 0"
					>
						<span class="primary-text">No Projects</span>
						<span fxLayoutAlign="start center">
							<mat-icon class="font-size-18 h-18 ml-4 pl-2 w-inherit">assignment</mat-icon>
						</span>
					</div>
				</div>
			</td>
			<!-- V2/V3 Layout: trailing placeholder reserved for the missing Assign Rep
			     slot in this row, so col widths align with row 1 across all rows. -->
			<td *ngIf="isAssignRepColumnVisibleInV2" fxFlex="14"></td>
		</tr>

		<!-- V1 only: expanded row with Main Email / Contact / Phone / Cell.
		     V2 surfaces Email / Contact / Phone in col 4 of the collapsed grid, so
		     this row is suppressed there to avoid duplication. -->
		<tr class="font-size-14 mt-3" fxLayout="row" *ngIf="isExpanded && !hideLastColumn">
			<td [fxFlex]="hideLastColumn ? (isAssignRepColumnVisibleInV2 ? '25' : '28') : ''" fxLayoutAlign="start center">
				<div
					fxLayoutAlign="end center"
					(click)="
						utilService.copyToClipboard(
							searchResult.businessEmailAddress,
							COPY_TO_CLIPBOARD_MSGS.EMAIL
						)
					"
					*ngIf="searchResult.businessEmailAddress"
				>
					<span class="text-nowrap">Main Email:</span>
					<span class="font-size-12 truncate_text">
						<a href="mailto:{{ searchResult.businessEmailAddress }}">{{
							searchResult.businessEmailAddress?.toLowerCase()
						}}</a>
					</span>
				</div>
			</td>
			<td [fxFlex]="hideLastColumn ? '19' : '20'" fxLayoutAlign="start center">
				<div
					fxLayoutAlign="space-between center"
					class="full-width p-r-1"
					*ngIf="searchResult.representative1"
				>
					<span class="text-nowrap">Contact:</span>
					<span class="font-size-12 truncate_text capitalize">{{
						searchResult.representative1?.toLowerCase()
					}}</span>
				</div>
			</td>
			<td [fxFlex]="hideLastColumn ? '19' : '21'">
				<div fxLayoutAlign="space-between center" class="full-width" *ngIf="searchResult.phone">
					<span>Phone:</span>
					<span
						><a href="tel:{{ searchResult.phone }}">{{ searchResult.phone }}</a></span
					>
				</div>
			</td>
			<td [fxFlex]="hideLastColumn ? '19' : '20'">
				<div
					fxLayoutAlign="space-between center"
					class="full-width p-r-1 p-l-1"
					*ngIf="searchResult.cellPhone"
				>
					<span>Cell:</span>
					<span
						><a href="tel:{{ searchResult.cellPhone }}">{{ searchResult.cellPhone }}</a></span
					>
				</div>
			</td>
			<!-- CN-8312 V2: empty trailing spacer is a V1-only Assign Rep slot.
			     Drop in V2 so the row sums to 100% and column widths apply. -->
			<td *ngIf="!hideLastColumn" [fxFlex]="hideLastColumn ? '19' : '11'"></td>
		</tr>
	</table>
</mat-panel-title>
